import React, { useEffect } from 'react';

import Archive from '@mui/icons-material/ArchiveRounded';
import {
  Grid,
  IconButton,
  Stack,
} from '@mui/material';

import { UserNote } from '../../services/users/types';
import Button from '../Button';
import EditField, { TypeOption } from '../EditField';
import NotesCard from '../NotesCard';

import {
  NoteForm,
} from './index.styles';

export interface NotesDisplayProps {
  notes?: UserNote[],
  onNoteAdd?: (value: string) => void,
  isNoteAddLoading?: boolean,
  isNoteAddSuccess?: boolean,
  onNoteDelete?: (noteCreationDatetime: number) => void,
  onNoteArchive?: (noteCreationDatetime: number) => void,
  onNoteRestore?: (noteCreationDatetime: number) => void,
  onNoteBillingRelatedToggle?: (noteCreationDatetime: number, billingRelated: boolean) => void,
  onNotePromote?: (noteCreationDatetime: number) => void,
  onNoteUpdate?: (noteCreationDatetime: number, value: string) => void,
  onNoteDueDateChange?: (noteCreationDatetime: number, value: number) => void,
  onNoteAssigneeChange?: (noteCreationDatetime: number, value: string) => void,
  isNoteUpdateLoading?: boolean,
  assigneeOptions?: TypeOption[],
}

const NotesDisplay = ({
  notes = [],
  onNoteAdd,
  isNoteAddLoading,
  isNoteAddSuccess,
  onNoteDelete,
  onNoteArchive,
  onNoteRestore,
  onNotePromote,
  onNoteBillingRelatedToggle,
  onNoteUpdate,
  onNoteDueDateChange,
  onNoteAssigneeChange,
  isNoteUpdateLoading,
  assigneeOptions,
}: NotesDisplayProps) => {
  const [newNote, setNewNote] = React.useState<string>('');
  const [sortedNotes, setSortedNotes] = React.useState<UserNote[]>([]);
  const [showArchived, setShowArchived] = React.useState<boolean>(false);

  const toggleShowArchived = () => {
    setShowArchived(!showArchived);
  };

  useEffect(() => {
    setSortedNotes([...notes]
      // TODO: Move state up to request for active/inactive notes from the parent
      // instead of filtering in the frontend
      .filter((note) => (showArchived ? !note.active : note.active))
      .sort((a, b) => {
        if (a?.priorityDatetime && b?.priorityDatetime) {
          return b.priorityDatetime - a.priorityDatetime;
        }
        if (a?.priorityDatetime) {
          return -1;
        }
        if (b?.priorityDatetime) {
          return 1;
        }
        return a.creationDatetime - b.creationDatetime;
      }));
  }, [notes, showArchived]);

  useEffect(() => {
    if (isNoteAddSuccess) {
      // We set the new note field as empty after confirming that
      // the note has been added successfully to prevent losing
      // progress
      setNewNote('');
    }
  }, [isNoteAddSuccess]);

  return (
    <Stack direction="column" spacing={1}>
      {sortedNotes.map((note) => (
        <NotesCard
          key={note.creationDatetime}
          note={note}
          onNoteDelete={onNoteDelete}
          onNoteArchive={onNoteArchive}
          onNoteRestore={onNoteRestore}
          onNotePromote={onNotePromote}
          onNoteUpdate={onNoteUpdate}
          onNoteBillingRelatedToggle={onNoteBillingRelatedToggle}
          onNoteDueDateChange={onNoteDueDateChange}
          onNoteAssigneeChange={onNoteAssigneeChange}
          assigneeOptions={assigneeOptions}
          isNoteUpdateLoading={isNoteUpdateLoading}
        />
      ))}
      {onNoteAdd && (
        <NoteForm>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <EditField
                placeholder="Enter Note..."
                value={newNote}
                onSubmit={(value) => setNewNote(value as string)}
                type="long"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" justifyContent="space-between">
                <div>
                  <Button
                    onClick={() => {
                      onNoteAdd(newNote);
                    }}
                    loading={isNoteAddLoading}
                    disabled={!newNote}
                  >
                    Add
                  </Button>
                </div>
                <IconButton
                  color={showArchived ? 'blue' : 'gray'}
                  size="small"
                  onClick={toggleShowArchived}
                >
                  <Archive />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </NoteForm>
      )}
    </Stack>
  );
};

export default NotesDisplay;
